/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const B = window, it = B.ShadowRoot && (B.ShadyCSS === void 0 || B.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, nt = Symbol(), lt = /* @__PURE__ */ new WeakMap();
let Ct = class {
  constructor(e, t, s) {
    if (this._$cssResult$ = !0, s !== nt) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = t;
  }
  get styleSheet() {
    let e = this.o;
    const t = this.t;
    if (it && e === void 0) {
      const s = t !== void 0 && t.length === 1;
      s && (e = lt.get(t)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), s && lt.set(t, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const ee = (e, ...t) => {
  const s = e.length === 1 ? e[0] : t.reduce((n, i, o) => n + ((r) => {
    if (r._$cssResult$ === !0) return r.cssText;
    if (typeof r == "number") return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + e[o + 1], e[0]);
  return new Ct(s, e, nt);
}, at = it ? (e) => e : (e) => e instanceof CSSStyleSheet ? ((t) => {
  let s = "";
  for (const n of t.cssRules) s += n.cssText;
  return ((n) => new Ct(typeof n == "string" ? n : n + "", void 0, nt))(s);
})(e) : e;
var J;
const z = window, ct = z.trustedTypes, Zt = ct ? ct.emptyScript : "", dt = z.reactiveElementPolyfillSupport, tt = { toAttribute(e, t) {
  switch (t) {
    case Boolean:
      e = e ? Zt : null;
      break;
    case Object:
    case Array:
      e = e == null ? e : JSON.stringify(e);
  }
  return e;
}, fromAttribute(e, t) {
  let s = e;
  switch (t) {
    case Boolean:
      s = e !== null;
      break;
    case Number:
      s = e === null ? null : Number(e);
      break;
    case Object:
    case Array:
      try {
        s = JSON.parse(e);
      } catch {
        s = null;
      }
  }
  return s;
} }, Ht = (e, t) => t !== e && (t == t || e == e), K = { attribute: !0, type: String, converter: tt, reflect: !1, hasChanged: Ht }, et = "finalized";
let C = class extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this._$Eu();
  }
  static addInitializer(e) {
    var t;
    this.finalize(), ((t = this.h) !== null && t !== void 0 ? t : this.h = []).push(e);
  }
  static get observedAttributes() {
    this.finalize();
    const e = [];
    return this.elementProperties.forEach((t, s) => {
      const n = this._$Ep(s, t);
      n !== void 0 && (this._$Ev.set(n, s), e.push(n));
    }), e;
  }
  static createProperty(e, t = K) {
    if (t.state && (t.attribute = !1), this.finalize(), this.elementProperties.set(e, t), !t.noAccessor && !this.prototype.hasOwnProperty(e)) {
      const s = typeof e == "symbol" ? Symbol() : "__" + e, n = this.getPropertyDescriptor(e, s, t);
      n !== void 0 && Object.defineProperty(this.prototype, e, n);
    }
  }
  static getPropertyDescriptor(e, t, s) {
    return { get() {
      return this[t];
    }, set(n) {
      const i = this[e];
      this[t] = n, this.requestUpdate(e, i, s);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) || K;
  }
  static finalize() {
    if (this.hasOwnProperty(et)) return !1;
    this[et] = !0;
    const e = Object.getPrototypeOf(this);
    if (e.finalize(), e.h !== void 0 && (this.h = [...e.h]), this.elementProperties = new Map(e.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const t = this.properties, s = [...Object.getOwnPropertyNames(t), ...Object.getOwnPropertySymbols(t)];
      for (const n of s) this.createProperty(n, t[n]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const s = new Set(e.flat(1 / 0).reverse());
      for (const n of s) t.unshift(at(n));
    } else e !== void 0 && t.push(at(e));
    return t;
  }
  static _$Ep(e, t) {
    const s = t.attribute;
    return s === !1 ? void 0 : typeof s == "string" ? s : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  _$Eu() {
    var e;
    this._$E_ = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (e = this.constructor.h) === null || e === void 0 || e.forEach((t) => t(this));
  }
  addController(e) {
    var t, s;
    ((t = this._$ES) !== null && t !== void 0 ? t : this._$ES = []).push(e), this.renderRoot !== void 0 && this.isConnected && ((s = e.hostConnected) === null || s === void 0 || s.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$ES) === null || t === void 0 || t.splice(this._$ES.indexOf(e) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((e, t) => {
      this.hasOwnProperty(t) && (this._$Ei.set(t, this[t]), delete this[t]);
    });
  }
  createRenderRoot() {
    var e;
    const t = (e = this.shadowRoot) !== null && e !== void 0 ? e : this.attachShadow(this.constructor.shadowRootOptions);
    return ((s, n) => {
      it ? s.adoptedStyleSheets = n.map((i) => i instanceof CSSStyleSheet ? i : i.styleSheet) : n.forEach((i) => {
        const o = document.createElement("style"), r = B.litNonce;
        r !== void 0 && o.setAttribute("nonce", r), o.textContent = i.cssText, s.appendChild(o);
      });
    })(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var e;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$ES) === null || e === void 0 || e.forEach((t) => {
      var s;
      return (s = t.hostConnected) === null || s === void 0 ? void 0 : s.call(t);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$ES) === null || e === void 0 || e.forEach((t) => {
      var s;
      return (s = t.hostDisconnected) === null || s === void 0 ? void 0 : s.call(t);
    });
  }
  attributeChangedCallback(e, t, s) {
    this._$AK(e, s);
  }
  _$EO(e, t, s = K) {
    var n;
    const i = this.constructor._$Ep(e, s);
    if (i !== void 0 && s.reflect === !0) {
      const o = (((n = s.converter) === null || n === void 0 ? void 0 : n.toAttribute) !== void 0 ? s.converter : tt).toAttribute(t, s.type);
      this._$El = e, o == null ? this.removeAttribute(i) : this.setAttribute(i, o), this._$El = null;
    }
  }
  _$AK(e, t) {
    var s;
    const n = this.constructor, i = n._$Ev.get(e);
    if (i !== void 0 && this._$El !== i) {
      const o = n.getPropertyOptions(i), r = typeof o.converter == "function" ? { fromAttribute: o.converter } : ((s = o.converter) === null || s === void 0 ? void 0 : s.fromAttribute) !== void 0 ? o.converter : tt;
      this._$El = i, this[i] = r.fromAttribute(t, o.type), this._$El = null;
    }
  }
  requestUpdate(e, t, s) {
    let n = !0;
    e !== void 0 && (((s = s || this.constructor.getPropertyOptions(e)).hasChanged || Ht)(this[e], t) ? (this._$AL.has(e) || this._$AL.set(e, t), s.reflect === !0 && this._$El !== e && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(e, s))) : n = !1), !this.isUpdatePending && n && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending) return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((n, i) => this[i] = n), this._$Ei = void 0);
    let t = !1;
    const s = this._$AL;
    try {
      t = this.shouldUpdate(s), t ? (this.willUpdate(s), (e = this._$ES) === null || e === void 0 || e.forEach((n) => {
        var i;
        return (i = n.hostUpdate) === null || i === void 0 ? void 0 : i.call(n);
      }), this.update(s)) : this._$Ek();
    } catch (n) {
      throw t = !1, this._$Ek(), n;
    }
    t && this._$AE(s);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$ES) === null || t === void 0 || t.forEach((s) => {
      var n;
      return (n = s.hostUpdated) === null || n === void 0 ? void 0 : n.call(s);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$EC !== void 0 && (this._$EC.forEach((t, s) => this._$EO(s, this[s], t)), this._$EC = void 0), this._$Ek();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
C[et] = !0, C.elementProperties = /* @__PURE__ */ new Map(), C.elementStyles = [], C.shadowRootOptions = { mode: "open" }, dt == null || dt({ ReactiveElement: C }), ((J = z.reactiveElementVersions) !== null && J !== void 0 ? J : z.reactiveElementVersions = []).push("1.6.3");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const U = globalThis, V = U.trustedTypes, pt = V ? V.createPolicy("lit-html", { createHTML: (e) => e }) : void 0, rt = "$lit$", v = `lit$${(Math.random() + "").slice(9)}$`, ot = "?" + v, qt = `<${ot}>`, E = document, W = () => E.createComment(""), M = (e) => e === null || typeof e != "object" && typeof e != "function", wt = Array.isArray, Nt = (e) => wt(e) || typeof (e == null ? void 0 : e[Symbol.iterator]) == "function", F = `[ 	
\f\r]`, N = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, $t = /-->/g, ut = />/g, f = RegExp(`>|${F}(?:([^\\s"'>=/]+)(${F}*=${F}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), _t = /'/g, At = /"/g, Tt = /^(?:script|style|textarea|title)$/i, R = Symbol.for("lit-noChange"), $ = Symbol.for("lit-nothing"), vt = /* @__PURE__ */ new WeakMap(), y = E.createTreeWalker(E, 129);
function Ut(e, t) {
  if (!Array.isArray(e) || !e.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return pt !== void 0 ? pt.createHTML(t) : t;
}
const Pt = (e, t) => {
  const s = e.length - 1, n = [];
  let i, o = t === 2 ? "<svg>" : "", r = N;
  for (let l = 0; l < s; l++) {
    const h = e[l];
    let d, c, a = -1, p = 0;
    for (; p < h.length && (r.lastIndex = p, c = r.exec(h), c !== null); ) p = r.lastIndex, r === N ? c[1] === "!--" ? r = $t : c[1] !== void 0 ? r = ut : c[2] !== void 0 ? (Tt.test(c[2]) && (i = RegExp("</" + c[2], "g")), r = f) : c[3] !== void 0 && (r = f) : r === f ? c[0] === ">" ? (r = i ?? N, a = -1) : c[1] === void 0 ? a = -2 : (a = r.lastIndex - c[2].length, d = c[1], r = c[3] === void 0 ? f : c[3] === '"' ? At : _t) : r === At || r === _t ? r = f : r === $t || r === ut ? r = N : (r = f, i = void 0);
    const _ = r === f && e[l + 1].startsWith("/>") ? " " : "";
    o += r === N ? h + qt : a >= 0 ? (n.push(d), h.slice(0, a) + rt + h.slice(a) + v + _) : h + v + (a === -2 ? l : _);
  }
  return [Ut(e, o + (e[s] || "<?>") + (t === 2 ? "</svg>" : "")), n];
};
let st = class Mt {
  constructor({ strings: t, _$litType$: s }, n) {
    let i;
    this.parts = [];
    let o = 0, r = 0;
    const l = t.length - 1, h = this.parts, [d, c] = Pt(t, s);
    if (this.el = Mt.createElement(d, n), y.currentNode = this.el.content, s === 2) {
      const a = this.el.content.firstChild;
      a.replaceWith(...a.childNodes);
    }
    for (; (i = y.nextNode()) !== null && h.length < l; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) for (const a of i.getAttributeNames()) if (a.endsWith(rt)) {
          const p = c[r++], _ = i.getAttribute(a).split(v), A = /([.?@])?(.*)/.exec(p);
          h.push({ type: 1, index: o, name: A[2], strings: _, ctor: A[1] === "." ? kt : A[1] === "?" ? It : A[1] === "@" ? jt : j }), i.removeAttribute(a);
        } else a.startsWith(v) && (h.push({ type: 6, index: o }), i.removeAttribute(a));
        if (Tt.test(i.tagName)) {
          const a = i.textContent.split(v), p = a.length - 1;
          if (p > 0) {
            i.textContent = V ? V.emptyScript : "";
            for (let _ = 0; _ < p; _++) i.append(a[_], W()), y.nextNode(), h.push({ type: 2, index: ++o });
            i.append(a[p], W());
          }
        }
      } else if (i.nodeType === 8) if (i.data === ot) h.push({ type: 2, index: o });
      else {
        let a = -1;
        for (; (a = i.data.indexOf(v, a + 1)) !== -1; ) h.push({ type: 7, index: o }), a += v.length - 1;
      }
      o++;
    }
  }
  static createElement(t, s) {
    const n = E.createElement("template");
    return n.innerHTML = t, n;
  }
};
function b(e, t, s = e, n) {
  var r, l;
  if (t === R) return t;
  let i = n !== void 0 ? (r = s._$Co) == null ? void 0 : r[n] : s._$Cl;
  const o = M(t) ? void 0 : t._$litDirective$;
  return (i == null ? void 0 : i.constructor) !== o && ((l = i == null ? void 0 : i._$AO) == null || l.call(i, !1), o === void 0 ? i = void 0 : (i = new o(e), i._$AT(e, s, n)), n !== void 0 ? (s._$Co ?? (s._$Co = []))[n] = i : s._$Cl = i), i !== void 0 && (t = b(e, i._$AS(e, t.values), i, n)), t;
}
let Rt = class {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: t }, parts: s } = this._$AD, n = ((e == null ? void 0 : e.creationScope) ?? E).importNode(t, !0);
    y.currentNode = n;
    let i = y.nextNode(), o = 0, r = 0, l = s[0];
    for (; l !== void 0; ) {
      if (o === l.index) {
        let h;
        l.type === 2 ? h = new ht(i, i.nextSibling, this, e) : l.type === 1 ? h = new l.ctor(i, l.name, l.strings, this, e) : l.type === 6 && (h = new Lt(i, this, e)), this._$AV.push(h), l = s[++r];
      }
      o !== (l == null ? void 0 : l.index) && (i = y.nextNode(), o++);
    }
    return y.currentNode = E, n;
  }
  p(e) {
    let t = 0;
    for (const s of this._$AV) s !== void 0 && (s.strings !== void 0 ? (s._$AI(e, s, t), t += s.strings.length - 2) : s._$AI(e[t])), t++;
  }
}, ht = class Ot {
  get _$AU() {
    var t;
    return ((t = this._$AM) == null ? void 0 : t._$AU) ?? this._$Cv;
  }
  constructor(t, s, n, i) {
    this.type = 2, this._$AH = $, this._$AN = void 0, this._$AA = t, this._$AB = s, this._$AM = n, this.options = i, this._$Cv = (i == null ? void 0 : i.isConnected) ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const s = this._$AM;
    return s !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = s.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, s = this) {
    t = b(this, t, s), M(t) ? t === $ || t == null || t === "" ? (this._$AH !== $ && this._$AR(), this._$AH = $) : t !== this._$AH && t !== R && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : Nt(t) ? this.k(t) : this._(t);
  }
  S(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.S(t));
  }
  _(t) {
    this._$AH !== $ && M(this._$AH) ? this._$AA.nextSibling.data = t : this.T(E.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    var o;
    const { values: s, _$litType$: n } = t, i = typeof n == "number" ? this._$AC(t) : (n.el === void 0 && (n.el = st.createElement(Ut(n.h, n.h[0]), this.options)), n);
    if (((o = this._$AH) == null ? void 0 : o._$AD) === i) this._$AH.p(s);
    else {
      const r = new Rt(i, this), l = r.u(this.options);
      r.p(s), this.T(l), this._$AH = r;
    }
  }
  _$AC(t) {
    let s = vt.get(t.strings);
    return s === void 0 && vt.set(t.strings, s = new st(t)), s;
  }
  k(t) {
    wt(this._$AH) || (this._$AH = [], this._$AR());
    const s = this._$AH;
    let n, i = 0;
    for (const o of t) i === s.length ? s.push(n = new Ot(this.S(W()), this.S(W()), this, this.options)) : n = s[i], n._$AI(o), i++;
    i < s.length && (this._$AR(n && n._$AB.nextSibling, i), s.length = i);
  }
  _$AR(t = this._$AA.nextSibling, s) {
    var n;
    for ((n = this._$AP) == null ? void 0 : n.call(this, !1, !0, s); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    var s;
    this._$AM === void 0 && (this._$Cv = t, (s = this._$AP) == null || s.call(this, t));
  }
}, j = class {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, s, n, i) {
    this.type = 1, this._$AH = $, this._$AN = void 0, this.element = e, this.name = t, this._$AM = n, this.options = i, s.length > 2 || s[0] !== "" || s[1] !== "" ? (this._$AH = Array(s.length - 1).fill(new String()), this.strings = s) : this._$AH = $;
  }
  _$AI(e, t = this, s, n) {
    const i = this.strings;
    let o = !1;
    if (i === void 0) e = b(this, e, t, 0), o = !M(e) || e !== this._$AH && e !== R, o && (this._$AH = e);
    else {
      const r = e;
      let l, h;
      for (e = i[0], l = 0; l < i.length - 1; l++) h = b(this, r[s + l], t, l), h === R && (h = this._$AH[l]), o || (o = !M(h) || h !== this._$AH[l]), h === $ ? e = $ : e !== $ && (e += (h ?? "") + i[l + 1]), this._$AH[l] = h;
    }
    o && !n && this.j(e);
  }
  j(e) {
    e === $ ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}, kt = class extends j {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === $ ? void 0 : e;
  }
}, It = class extends j {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== $);
  }
}, jt = class extends j {
  constructor(e, t, s, n, i) {
    super(e, t, s, n, i), this.type = 5;
  }
  _$AI(e, t = this) {
    if ((e = b(this, e, t, 0) ?? $) === R) return;
    const s = this._$AH, n = e === $ && s !== $ || e.capture !== s.capture || e.once !== s.once || e.passive !== s.passive, i = e !== $ && (s === $ || n);
    n && this.element.removeEventListener(this.name, this, s), i && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t;
    typeof this._$AH == "function" ? this._$AH.call(((t = this.options) == null ? void 0 : t.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}, Lt = class {
  constructor(e, t, s) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = s;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    b(this, e);
  }
};
const se = { P: rt, A: v, C: ot, M: 1, L: Pt, R: Rt, D: Nt, V: b, I: ht, H: j, N: It, U: jt, B: kt, F: Lt }, G = U.litHtmlPolyfillSupport;
G == null || G(st, ht), (U.litHtmlVersions ?? (U.litHtmlVersions = [])).push("3.1.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const P = globalThis, Z = P.trustedTypes, gt = Z ? Z.createPolicy("lit-html", { createHTML: (e) => e }) : void 0, Bt = "$lit$", g = `lit$${(Math.random() + "").slice(9)}$`, Dt = "?" + g, Jt = `<${Dt}>`, x = document, O = () => x.createComment(""), k = (e) => e === null || typeof e != "object" && typeof e != "function", ft = Array.isArray, Q = `[ 	
\f\r]`, T = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, mt = /-->/g, yt = />/g, m = RegExp(`>|${Q}(?:([^\\s"'>=/]+)(${Q}*=${Q}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), St = /'/g, Et = /"/g, zt = /^(?:script|style|textarea|title)$/i, Vt = (e) => (t, ...s) => ({ _$litType$: e, strings: t, values: s }), ie = Vt(1), ne = Vt(2), H = Symbol.for("lit-noChange"), u = Symbol.for("lit-nothing"), bt = /* @__PURE__ */ new WeakMap(), S = x.createTreeWalker(x, 129);
function Wt(e, t) {
  if (!Array.isArray(e) || !e.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return gt !== void 0 ? gt.createHTML(t) : t;
}
const Kt = (e, t) => {
  const s = e.length - 1, n = [];
  let i, o = t === 2 ? "<svg>" : "", r = T;
  for (let l = 0; l < s; l++) {
    const h = e[l];
    let d, c, a = -1, p = 0;
    for (; p < h.length && (r.lastIndex = p, c = r.exec(h), c !== null); ) p = r.lastIndex, r === T ? c[1] === "!--" ? r = mt : c[1] !== void 0 ? r = yt : c[2] !== void 0 ? (zt.test(c[2]) && (i = RegExp("</" + c[2], "g")), r = m) : c[3] !== void 0 && (r = m) : r === m ? c[0] === ">" ? (r = i ?? T, a = -1) : c[1] === void 0 ? a = -2 : (a = r.lastIndex - c[2].length, d = c[1], r = c[3] === void 0 ? m : c[3] === '"' ? Et : St) : r === Et || r === St ? r = m : r === mt || r === yt ? r = T : (r = m, i = void 0);
    const _ = r === m && e[l + 1].startsWith("/>") ? " " : "";
    o += r === T ? h + Jt : a >= 0 ? (n.push(d), h.slice(0, a) + Bt + h.slice(a) + g + _) : h + g + (a === -2 ? l : _);
  }
  return [Wt(e, o + (e[s] || "<?>") + (t === 2 ? "</svg>" : "")), n];
};
class I {
  constructor({ strings: t, _$litType$: s }, n) {
    let i;
    this.parts = [];
    let o = 0, r = 0;
    const l = t.length - 1, h = this.parts, [d, c] = Kt(t, s);
    if (this.el = I.createElement(d, n), S.currentNode = this.el.content, s === 2) {
      const a = this.el.content.firstChild;
      a.replaceWith(...a.childNodes);
    }
    for (; (i = S.nextNode()) !== null && h.length < l; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) for (const a of i.getAttributeNames()) if (a.endsWith(Bt)) {
          const p = c[r++], _ = i.getAttribute(a).split(g), A = /([.?@])?(.*)/.exec(p);
          h.push({ type: 1, index: o, name: A[2], strings: _, ctor: A[1] === "." ? Gt : A[1] === "?" ? Qt : A[1] === "@" ? Xt : q }), i.removeAttribute(a);
        } else a.startsWith(g) && (h.push({ type: 6, index: o }), i.removeAttribute(a));
        if (zt.test(i.tagName)) {
          const a = i.textContent.split(g), p = a.length - 1;
          if (p > 0) {
            i.textContent = Z ? Z.emptyScript : "";
            for (let _ = 0; _ < p; _++) i.append(a[_], O()), S.nextNode(), h.push({ type: 2, index: ++o });
            i.append(a[p], O());
          }
        }
      } else if (i.nodeType === 8) if (i.data === Dt) h.push({ type: 2, index: o });
      else {
        let a = -1;
        for (; (a = i.data.indexOf(g, a + 1)) !== -1; ) h.push({ type: 7, index: o }), a += g.length - 1;
      }
      o++;
    }
  }
  static createElement(t, s) {
    const n = x.createElement("template");
    return n.innerHTML = t, n;
  }
}
function w(e, t, s = e, n) {
  var r, l;
  if (t === H) return t;
  let i = n !== void 0 ? (r = s._$Co) == null ? void 0 : r[n] : s._$Cl;
  const o = k(t) ? void 0 : t._$litDirective$;
  return (i == null ? void 0 : i.constructor) !== o && ((l = i == null ? void 0 : i._$AO) == null || l.call(i, !1), o === void 0 ? i = void 0 : (i = new o(e), i._$AT(e, s, n)), n !== void 0 ? (s._$Co ?? (s._$Co = []))[n] = i : s._$Cl = i), i !== void 0 && (t = w(e, i._$AS(e, t.values), i, n)), t;
}
class Ft {
  constructor(t, s) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = s;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: s }, parts: n } = this._$AD, i = ((t == null ? void 0 : t.creationScope) ?? x).importNode(s, !0);
    S.currentNode = i;
    let o = S.nextNode(), r = 0, l = 0, h = n[0];
    for (; h !== void 0; ) {
      if (r === h.index) {
        let d;
        h.type === 2 ? d = new L(o, o.nextSibling, this, t) : h.type === 1 ? d = new h.ctor(o, h.name, h.strings, this, t) : h.type === 6 && (d = new Yt(o, this, t)), this._$AV.push(d), h = n[++l];
      }
      r !== (h == null ? void 0 : h.index) && (o = S.nextNode(), r++);
    }
    return S.currentNode = x, i;
  }
  p(t) {
    let s = 0;
    for (const n of this._$AV) n !== void 0 && (n.strings !== void 0 ? (n._$AI(t, n, s), s += n.strings.length - 2) : n._$AI(t[s])), s++;
  }
}
class L {
  get _$AU() {
    var t;
    return ((t = this._$AM) == null ? void 0 : t._$AU) ?? this._$Cv;
  }
  constructor(t, s, n, i) {
    this.type = 2, this._$AH = u, this._$AN = void 0, this._$AA = t, this._$AB = s, this._$AM = n, this.options = i, this._$Cv = (i == null ? void 0 : i.isConnected) ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const s = this._$AM;
    return s !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = s.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, s = this) {
    t = w(this, t, s), k(t) ? t === u || t == null || t === "" ? (this._$AH !== u && this._$AR(), this._$AH = u) : t !== this._$AH && t !== H && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : ((n) => ft(n) || typeof (n == null ? void 0 : n[Symbol.iterator]) == "function")(t) ? this.k(t) : this._(t);
  }
  S(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.S(t));
  }
  _(t) {
    this._$AH !== u && k(this._$AH) ? this._$AA.nextSibling.data = t : this.T(x.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    var o;
    const { values: s, _$litType$: n } = t, i = typeof n == "number" ? this._$AC(t) : (n.el === void 0 && (n.el = I.createElement(Wt(n.h, n.h[0]), this.options)), n);
    if (((o = this._$AH) == null ? void 0 : o._$AD) === i) this._$AH.p(s);
    else {
      const r = new Ft(i, this), l = r.u(this.options);
      r.p(s), this.T(l), this._$AH = r;
    }
  }
  _$AC(t) {
    let s = bt.get(t.strings);
    return s === void 0 && bt.set(t.strings, s = new I(t)), s;
  }
  k(t) {
    ft(this._$AH) || (this._$AH = [], this._$AR());
    const s = this._$AH;
    let n, i = 0;
    for (const o of t) i === s.length ? s.push(n = new L(this.S(O()), this.S(O()), this, this.options)) : n = s[i], n._$AI(o), i++;
    i < s.length && (this._$AR(n && n._$AB.nextSibling, i), s.length = i);
  }
  _$AR(t = this._$AA.nextSibling, s) {
    var n;
    for ((n = this._$AP) == null ? void 0 : n.call(this, !1, !0, s); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    var s;
    this._$AM === void 0 && (this._$Cv = t, (s = this._$AP) == null || s.call(this, t));
  }
}
class q {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, s, n, i, o) {
    this.type = 1, this._$AH = u, this._$AN = void 0, this.element = t, this.name = s, this._$AM = i, this.options = o, n.length > 2 || n[0] !== "" || n[1] !== "" ? (this._$AH = Array(n.length - 1).fill(new String()), this.strings = n) : this._$AH = u;
  }
  _$AI(t, s = this, n, i) {
    const o = this.strings;
    let r = !1;
    if (o === void 0) t = w(this, t, s, 0), r = !k(t) || t !== this._$AH && t !== H, r && (this._$AH = t);
    else {
      const l = t;
      let h, d;
      for (t = o[0], h = 0; h < o.length - 1; h++) d = w(this, l[n + h], s, h), d === H && (d = this._$AH[h]), r || (r = !k(d) || d !== this._$AH[h]), d === u ? t = u : t !== u && (t += (d ?? "") + o[h + 1]), this._$AH[h] = d;
    }
    r && !i && this.j(t);
  }
  j(t) {
    t === u ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Gt extends q {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === u ? void 0 : t;
  }
}
class Qt extends q {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== u);
  }
}
class Xt extends q {
  constructor(t, s, n, i, o) {
    super(t, s, n, i, o), this.type = 5;
  }
  _$AI(t, s = this) {
    if ((t = w(this, t, s, 0) ?? u) === H) return;
    const n = this._$AH, i = t === u && n !== u || t.capture !== n.capture || t.once !== n.once || t.passive !== n.passive, o = t !== u && (n === u || i);
    i && this.element.removeEventListener(this.name, this, n), o && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var s;
    typeof this._$AH == "function" ? this._$AH.call(((s = this.options) == null ? void 0 : s.host) ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Yt {
  constructor(t, s, n) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = s, this.options = n;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    w(this, t);
  }
}
const X = P.litHtmlPolyfillSupport;
X == null || X(I, L), (P.litHtmlVersions ?? (P.litHtmlVersions = [])).push("3.1.2");
const te = (e, t, s) => {
  const n = (s == null ? void 0 : s.renderBefore) ?? t;
  let i = n._$litPart$;
  if (i === void 0) {
    const o = (s == null ? void 0 : s.renderBefore) ?? null;
    n._$litPart$ = i = new L(t.insertBefore(O(), o), o, void 0, s ?? {});
  }
  return i._$AI(e), i;
  /**
   * @license
   * Copyright 2017 Google LLC
   * SPDX-License-Identifier: BSD-3-Clause
   */
};
class D extends C {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var s;
    const t = super.createRenderRoot();
    return (s = this.renderOptions).renderBefore ?? (s.renderBefore = t.firstChild), t;
  }
  update(t) {
    const s = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = te(s, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) == null || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) == null || t.setConnected(!1);
  }
  render() {
    return H;
  }
}
var xt;
D._$litElement$ = !0, D.finalized = !0, (xt = globalThis.litElementHydrateSupport) == null || xt.call(globalThis, { LitElement: D });
const Y = globalThis.litElementPolyfillSupport;
Y == null || Y({ LitElement: D }), (globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.0.4");
export {
  u as T,
  $ as a,
  ne as b,
  H as c,
  ee as i,
  te as j,
  D as s,
  R as w,
  ie as x,
  se as z
};
//# sourceMappingURL=lit-element-6iugfgmM.js.map
